/**
 * VehiclePlanBlock — the Vehicles block inside Job Details (transport
 * rebuild). Which vehicles the job takes, plus the allocation-conflict
 * warning. Pure planning — nothing here reads or writes the quote's priced
 * Transport line, which lives in the gear editor.
 *
 * It carries no times: when the fleet is out is a pair of columns on the day
 * schedule directly above it (use-vehicle-day-times), because every vehicle on
 * a job leaves and returns together and a second time grid asked the same
 * question twice.
 */
import { XIcon } from "../ui/icons";
import { InlineBanner } from "../ui/InlineBanner";
import { useBookingTransport, VehicleAutocomplete } from "./TransportSection";
import { AllocationConflictNotice } from "./AllocationConflictNotice";
import { type VehicleConflict } from "../../lib/allocation-api";
import forms from "../ui/field.module.css";
import styles from "./VehiclePlanBlock.module.css";

interface VehiclePlanBlockProps {
  bookingId: string;
  /**
   * Vehicles this Booking shares with another Booking in a reserving Phase on
   * overlapping dates (#305). Warn-only: the Operator often intends it — a
   * truck legitimately does two short local jobs in a day.
   */
  vehicleConflicts?: VehicleConflict[];
  /** Refetch the workspace after a selection change — job sheets and
   * staleness read the planned vehicles, and a new one needs the day
   * schedule's vehicle times fanned out to it. */
  onRefresh: () => void | Promise<void>;
}

export function VehiclePlanBlock({
  bookingId,
  vehicleConflicts = [],
  onRefresh,
}: VehiclePlanBlockProps) {
  const transport = useBookingTransport({
    bookingId,
    onChanged: async () => {
      await onRefresh();
    },
  });

  const conflictsByVehicle = new Map(
    vehicleConflicts.map((c) => [c.vehicle_id, c.clashes]),
  );

  return (
    <>
      {/* Same shape as the crew picker above: one search that appends to the
          list below it. Both are "pick from a Company list", so they read the
          same way. */}
      <div className={styles.search}>
        <VehicleAutocomplete
          options={transport.availableFleet}
          disabled={!transport.loaded || transport.busy}
          className={forms.input}
          clearOnSelect
          onSelect={(v) => transport.addVehicle(v)}
        />
      </div>

      {transport.error && (
        <InlineBanner
          variant="error"
          placement="none"
          className={styles.error}
        >
          {transport.error}
        </InlineBanner>
      )}

      {transport.selection.length === 0 ? (
        <p className={styles.empty}>
          No vehicles planned. Select a vehicle above.
        </p>
      ) : (
        <div className={styles.list}>
          {transport.selection.map((v) => (
            <div key={v.vehicle_id} className={styles.row}>
              <div className={styles.rowHead}>
                <span className={styles.name}>{v.vehicle_name}</span>
                {v.vehicle_type && (
                  <span className={styles.type}>{v.vehicle_type}</span>
                )}
                <button
                  type="button"
                  onClick={() => transport.removeVehicle(v.vehicle_id)}
                  disabled={transport.busy}
                  aria-label={`Remove ${v.vehicle_name}`}
                  className={styles.remove}
                >
                  <XIcon className={styles.removeIcon} />
                </button>
              </div>
              <AllocationConflictNotice
                lead="Also out on"
                clashes={conflictsByVehicle.get(v.vehicle_id) ?? []}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
